import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import ConsultationForm from '../components/ConsultationForm';
import '../styles/Estimator.css';

const EstimatorWardrobe = () => {
  const [city, setCity] = useState('Delhi');
  const [width, setWidth] = useState('6');
  const [height, setHeight] = useState('8');
  const [finish, setFinish] = useState('Laminates');
  const [type, setType] = useState('Hinged');
  const [quoteOpen, setQuoteOpen] = useState(false);

  const estimate = useMemo(() => {
    const w = Math.max(0, Number(width) || 0);
    const h = Math.max(0, Number(height) || 0);

    // Base rate per sqft of shutter face area (approx):
    const finishPerSqft = {
      Laminates: 950,
      Acrylic: 1450,
      Glass: 1700,
    }[finish] || 950;

    // Sliding is costlier
    const typeMultiplier = type === 'Sliding' ? 1.25 : 1.0;

    // Approximate shutter face area for a 2-door module = width x height
    const shutterArea = w * h; // sqft (ft x ft)
    const shuttersCost = shutterArea * finishPerSqft * typeMultiplier;

    // Carcass allowance per running ft
    const carcassPerFt = 1800;
    const carcass = w * carcassPerFt;

    // Hardware allowance
    const hardware = w * 600;

    const base = Math.max(0, Math.round(shuttersCost + carcass + hardware));
    const misc = Math.round(base * 0.05);
    const total = base + misc;
    const low = Math.round(total * 0.92);
    const high = Math.round(total * 1.08);
    return { low, high, total, base, carcass, hardware, misc };
  }, [width, height, finish, type]);

  return (
    <section className="estimator-detail">
      <div className="container">
        {/* <nav aria-label="Breadcrumb" style={{marginBottom: '0.75rem'}}>
          <Link to="/estimator" className="link">← Back to Estimator</Link>
        </nav> */}
        <div className="estimator-header">
          <h1 className="section-title">Wardrobe Estimator</h1>
          <p className="section-subtitle">Estimate the cost of modular wardrobes by size, shutter finish and accessories—with light-touch Vastu and zodiac suggestions.</p>
          <div style={{ marginTop: '0.5rem', display: 'flex', gap: '0.5rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <span className="chip" aria-label="Typical timeline">2–3 weeks</span>
            <span className="chip" aria-label="Design style">Modern</span>
          </div>
        </div>

        <div className="estimator-content">
          <div className="estimator-summary">
            <h3>What’s included</h3>
            <ul>
              <li>Carcass and shutters</li>
              <li>Hardware and hinges</li>
              <li>Basic internal partitions</li>
              <li>Accessories (optional)</li>
            </ul>
            <div style={{ marginTop: '1rem' }} role="status" aria-live="polite">
              <strong>Estimated range:</strong>
              <div style={{ marginTop: '0.25rem', fontSize: '1.05rem' }}>
                ₹{estimate.low.toLocaleString('en-IN')} — ₹{estimate.high.toLocaleString('en-IN')}
              </div>
              <div style={{ marginTop: '0.25rem', color: 'var(--text-secondary)' }}>Mid: ₹{estimate.total.toLocaleString('en-IN')}</div>
              <ul style={{ marginTop: '0.5rem', paddingLeft: '1rem', color: 'var(--text-secondary)' }}>
                <li>Shutters + finish: ₹{estimate.base.toLocaleString('en-IN')}</li>
                <li>Carcass: ₹{estimate.carcass.toLocaleString('en-IN')}</li>
                <li>Hardware: ₹{estimate.hardware.toLocaleString('en-IN')}</li>
                <li>Contingency (5%): ₹{estimate.misc.toLocaleString('en-IN')}</li>
              </ul>
              <p style={{ marginTop: '0.5rem', color: 'var(--text-secondary)', fontSize: '0.95rem' }}>
                Note: Estimates assume standard internal partitions; accessories and mirrors are optional add-ons.
              </p>
            </div>
          </div>

          <div className="estimator-form">
            <h3>Basic details</h3>
            <div className="form-row">
              <div className="form-field">
                <label>City</label>
                <select value={city} onChange={(e) => setCity(e.target.value)}>
                  <option value="Delhi">Delhi</option>
                  <option value="New Delhi">New Delhi</option>
                  <option value="Gurugram">Gurugram</option>
                  <option value="Noida">Noida</option>
                  <option value="Greater Noida">Greater Noida</option>
                  <option value="Ghaziabad">Ghaziabad</option>
                  <option value="Faridabad">Faridabad</option>
                  <option value="Dwarka">Dwarka</option>
                  <option value="Rohini">Rohini</option>
                  <option value="Saket">Saket</option>
                </select>
              </div>
            </div>
            <div className="form-row">
              <div className="form-field">
                <label>Width (ft)</label>
                <input
                  type="number"
                  placeholder="e.g., 6"
                  value={width}
                  min={0}
                  onChange={(e) => setWidth(e.target.value)}
                />
              </div>
              <div className="form-field">
                <label>Height (ft)</label>
                <input
                  type="number"
                  placeholder="e.g., 8"
                  value={height}
                  min={0}
                  onChange={(e) => setHeight(e.target.value)}
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-field">
                <label>Shutter finish</label>
                <select value={finish} onChange={(e) => setFinish(e.target.value)}>
                  <option value="Laminates">Laminates</option>
                  <option value="Acrylic">Acrylic</option>
                  <option value="Glass">Glass</option>
                </select>
              </div>
              <div className="form-field">
                <label>Type</label>
                <select value={type} onChange={(e) => setType(e.target.value)}>
                  <option value="Hinged">Hinged</option>
                  <option value="Sliding">Sliding</option>
                </select>
              </div>
            </div>
            <div className="estimator-actions">
              <button className="btn btn-primary" type="button">Estimate updated</button>
              <button className="btn btn-outline" type="button" onClick={() => setQuoteOpen(true)}>
                Get a detailed quote
              </button>
            </div>
          </div>
        </div>
      </div>
      <ConsultationForm
        isOpen={quoteOpen}
        onClose={() => setQuoteOpen(false)}
        type="quote"
        initialService="Wardrobe"
      />
    </section>
  );
};

export default EstimatorWardrobe;
